import React, { FC, useState } from "react";
import { Form, Modal, ModalProps, notification, Tag } from "antd";
import { useIntl } from "react-intl";
import { Maybe } from "graphql/jsutils/Maybe";
import {
  Core_KnowledgeInputMsg,
  Knowledge_KnowledgeDataset,
  Resource_AppointmentRequestMsg,
  Resource_AppointmentRequestStatus,
  useCancelAppointmentRequestMutation,
} from "../../src/generated/graphql";
import { GeneralKnowledgeBaseMultiInput } from "../common-layout/general-knowledge-input";
import styles from "../appointment-details/appointment-details.module.scss";
import { CustomTagProps } from "rc-select/es/BaseSelect";
import { AveliosButton } from "../common-layout/avelios-button";
import { useRouter } from "next/router";

interface CancelAppointmentRequestModalProps extends ModalProps {
  appointmentRequest: Maybe<Resource_AppointmentRequestMsg> | undefined;
  clinicId: Maybe<string> | undefined;
}

export const CancelAppointmentRequestModal: FC<
  CancelAppointmentRequestModalProps
> = ({ appointmentRequest, clinicId, ...props }) => {
  const { formatMessage } = useIntl();

  const router = useRouter();

  const [reasonsForCancellation, setReasonsForCancellation] = useState<
    Maybe<(Maybe<Core_KnowledgeInputMsg> | undefined)[]> | undefined
  >();

  const [cancelAppointmentRequestMutation, { loading: cancelLoading }] =
    useCancelAppointmentRequestMutation({
      onCompleted: (data) => {
        if (
          data.cancelAppointmentRequest?.appointmentRequest?.status ===
          Resource_AppointmentRequestStatus.AppointmentRequestStatusCancelled
        ) {
          // @ts-ignore
          props.onCancel();
          notification.info({
            message: formatMessage({
              id: "cancel-appointment-request.success",
            }),
          });
          router.push("/appointments");
        } else {
          notification.error({
            message: formatMessage({ id: "notification.error" }),
          });
        }
      },
      onError: () => {
        notification.error({
          message: formatMessage({ id: "notification.error" }),
        });
      },
    });
  function cancelAppointment() {
    if (appointmentRequest?.id) {
      cancelAppointmentRequestMutation({
        variables: {
          clinicId: clinicId!,
          appointmentRequestId: appointmentRequest?.id,
          reasonsForCancellation:
            reasonsForCancellation?.map((reason) => reason!) || [],
        },
      });
    }
  }

  const tagRender = (props: CustomTagProps) => {
    const { label, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        className={styles.reasonForCancellationTag}
        color={"default"}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
      >
        {label}
      </Tag>
    );
  };

  return (
    <Modal
      title={formatMessage({ id: "cancel-appointment-request.title" })}
      footer={
        <AveliosButton
          loading={cancelLoading}
          type={"primary"}
          danger
          onClick={cancelAppointment}
        >
          {formatMessage({ id: "appointment-request-cancel" })}
        </AveliosButton>
      }
      {...props}
    >
      <Form layout={"vertical"}>
        <Form.Item
          label={formatMessage({ id: "cancel-appointment-request.reason" })}
        >
          <GeneralKnowledgeBaseMultiInput
            className={styles.reasonForCancellation}
            // @ts-ignore
            values={reasonsForCancellation}
            dataset={
              Knowledge_KnowledgeDataset.DatasetReasonForPatientAppointmentCancellation
            }
            tagRender={tagRender}
            size={"large"}
            setValues={setReasonsForCancellation}
            clinicId={clinicId}
            placeholder={formatMessage({
              id: "reason-for-cancellation.placeholder",
            })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
